import { createApi } from "@reduxjs/toolkit/query/react";
import { API_SERVER } from "../utils/urlBack_End";
import baseQuery from "./baseQuery";

export const conciliacionApi = createApi({
  reducerPath: "conciliacionApi",
  baseQuery: baseQuery(`${API_SERVER}/conciliacion`),
  tagTypes: [
    "Lotes",
    "Movimientos",
    "Sugerencias",
    "Conciliaciones",
    "RutsAlternativos",
    "AbonosPendientes",
    "CategoriasContables",
  ],
  endpoints: (builder) => ({
    // ============================================
    // CARGA DE CARTOLAS
    // ============================================

    uploadCartola: builder.mutation({
      query: (formData) => ({
        url: "/upload",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["Lotes", "Movimientos"],
    }),

    // ============================================
    // GESTIÓN DE LOTES
    // ============================================

    obtenerLotes: builder.query({
      query: (params) => ({
        url: "/lotes",
        params,
      }),
      providesTags: ["Lotes"],
    }),

    // ============================================
    // GESTIÓN DE MOVIMIENTOS
    // ============================================

    obtenerMovimientos: builder.query({
      query: (params) => ({
        url: "/movimientos",
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: "Movimientos", id })),
              { type: "Movimientos", id: "LIST" },
            ]
          : [{ type: "Movimientos", id: "LIST" }],
    }),

    // ============================================
    // MATCHING AUTOMÁTICO
    // ============================================

    procesarAutomatico: builder.mutation({
      query: (body) => ({
        url: "/procesar-automatico",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Movimientos", "Lotes", "Conciliaciones"],
    }),

    obtenerSugerencias: builder.query({
      query: (movimientoId) => ({
        url: `/sugerencias/${movimientoId}`,
      }),
      providesTags: (result, error, id) => [{ type: "Sugerencias", id }],
    }),

    // ============================================
    // CONCILIACIÓN MANUAL
    // ============================================

    conciliarManual: builder.mutation({
      query: (body) => ({
        url: "/conciliar-manual",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "Movimientos", id: "LIST" },
        { type: "Conciliaciones", id: "LIST" },
      ],
    }),

    rechazarMovimiento: builder.mutation({
      query: (body) => ({
        url: "/rechazar",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Movimientos", id: "LIST" }],
    }),

    revertirConciliacion: builder.mutation({
      query: (body) => ({
        url: "/revertir",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "Movimientos", id: "LIST" },
        { type: "Conciliaciones", id: "LIST" },
      ],
    }),

    reactivarMovimiento: builder.mutation({
      query: (body) => ({
        url: "/reactivar",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Movimientos", id: "LIST" }],
    }),

    // ============================================
    // HISTORIAL DE CONCILIACIONES
    // ============================================

    obtenerConciliaciones: builder.query({
      query: (params) => ({
        url: "/conciliaciones",
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: "Conciliaciones", id })),
              { type: "Conciliaciones", id: "LIST" },
            ]
          : [{ type: "Conciliaciones", id: "LIST" }],
    }),

    // ============================================
    // OPERACIONES MASIVAS (BULK)
    // ============================================

    bulkConciliar: builder.mutation({
      query: (body) => ({
        url: "/bulk/conciliar",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "Movimientos", id: "LIST" },
        { type: "Conciliaciones", id: "LIST" },
        "Lotes",
      ],
    }),

    bulkReactivar: builder.mutation({
      query: (body) => ({
        url: "/bulk/reactivar",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Movimientos", id: "LIST" }],
    }),

    bulkRechazar: builder.mutation({
      query: (body) => ({
        url: "/bulk/rechazar",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Movimientos", id: "LIST" }],
    }),

    bulkRevertir: builder.mutation({
      query: (body) => ({
        url: "/bulk/revertir",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "Movimientos", id: "LIST" },
        { type: "Conciliaciones", id: "LIST" },
      ],
    }),

    // ============================================
    // RUTS ALTERNATIVOS
    // ============================================

    crearRutAlternativo: builder.mutation({
      query: (body) => ({
        url: "/ruts-alternativos",
        method: "POST",
        body,
      }),
      invalidatesTags: ["RutsAlternativos"],
    }),

    obtenerRutsAlternativos: builder.query({
      query: (refCliente) => ({
        url: `/ruts-alternativos/${refCliente}`,
      }),
      providesTags: (result, error, refCliente) => [
        { type: "RutsAlternativos", id: refCliente },
      ],
    }),

    eliminarRutAlternativo: builder.mutation({
      query: (id) => ({
        url: `/ruts-alternativos/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["RutsAlternativos"],
    }),

    toggleRutAlternativo: builder.mutation({
      query: ({ id, activo }) => ({
        url: `/ruts-alternativos/${id}/toggle`,
        method: "PUT",
        body: { activo },
      }),
      invalidatesTags: ["RutsAlternativos"],
    }),

    // ============================================
    // BÚSQUEDA DE FACTURAS
    // ============================================

    buscarFacturas: builder.query({
      query: (params) => ({
        url: "/buscar-facturas",
        params,
      }),
    }),

    // ============================================
    // GESTIÓN DE ABONOS
    // ============================================

    cerrarAbono: builder.mutation({
      query: (body) => ({
        url: "/cerrar-abono",
        method: "POST",
        body,
      }),
      invalidatesTags: ["AbonosPendientes"],
    }),

    obtenerAbonosPendientes: builder.query({
      query: (params) => ({
        url: "/abonos-pendientes",
        params,
      }),
      providesTags: ["AbonosPendientes"],
    }),

    aplicarAbono: builder.mutation({
      query: (body) => ({
        url: "/aplicar-abono",
        method: "POST",
        body,
      }),
      invalidatesTags: ["AbonosPendientes", "Movimientos", "Conciliaciones"],
    }),

    // ============================================
    // CATEGORÍAS CONTABLES
    // ============================================

    obtenerCategoriasContables: builder.query({
      query: (params) => ({
        url: "/categorias-contables",
        params, // Permite filtros: activo, tipo
      }),
      providesTags: ["CategoriasContables"],
    }),

    obtenerCategoriaContable: builder.query({
      query: (id) => ({
        url: `/categorias-contables/${id}`,
      }),
      providesTags: (result, error, id) => [
        { type: "CategoriasContables", id },
      ],
    }),

    crearCategoriaContable: builder.mutation({
      query: (body) => ({
        url: "/categorias-contables",
        method: "POST",
        body,
      }),
      invalidatesTags: ["CategoriasContables"],
    }),

    actualizarCategoriaContable: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/categorias-contables/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["CategoriasContables"],
    }),

    eliminarCategoriaContable: builder.mutation({
      query: (id) => ({
        url: `/categorias-contables/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["CategoriasContables"],
    }),

    toggleCategoriaContable: builder.mutation({
      query: (id) => ({
        url: `/categorias-contables/${id}/toggle`,
        method: "PATCH",
      }),
      invalidatesTags: ["CategoriasContables"],
    }),
  }),
});

export const {
  // Carga
  useUploadCartolaMutation,

  // Lotes
  useObtenerLotesQuery,
  useLazyObtenerLotesQuery,

  // Movimientos
  useObtenerMovimientosQuery,
  useLazyObtenerMovimientosQuery,

  // Matching
  useProcesarAutomaticoMutation,
  useObtenerSugerenciasQuery,
  useLazyObtenerSugerenciasQuery,

  // Conciliación
  useConciliarManualMutation,
  useRechazarMovimientoMutation,
  useRevertirConciliacionMutation,

  // Historial
  useObtenerConciliacionesQuery,
  useLazyObtenerConciliacionesQuery,

  // Bulk
  useBulkConciliarMutation,
  useBulkRechazarMutation,
  useBulkRevertirMutation,

  // Reactivar
  useReactivarMovimientoMutation,
  useBulkReactivarMutation,

  // RUTs Alternativos
  useCrearRutAlternativoMutation,
  useObtenerRutsAlternativosQuery,
  useLazyObtenerRutsAlternativosQuery,
  useEliminarRutAlternativoMutation,
  useToggleRutAlternativoMutation,

  // Búsqueda
  useBuscarFacturasQuery,
  useLazyBuscarFacturasQuery,

  // Abonos
  useObtenerAbonosPendientesQuery,
  useLazyObtenerAbonosPendientesQuery,
  useAplicarAbonoMutation,
  useCerrarAbonoMutation,

  // Categorías Contables
  useCrearCategoriaContableMutation,
  useObtenerCategoriaContableQuery,
  useLazyObtenerCategoriaContableQuery,
  useObtenerCategoriasContablesQuery,
  useActualizarCategoriaContableMutation,
  useEliminarCategoriaContableMutation,
  useToggleCategoriaContableMutation,
} = conciliacionApi;
